import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  Dropdown,
  Spinner,
  Table,
} from "react-bootstrap";
import { Empty, Input } from "antd";
import { toast } from "react-toastify";
import IconButton from "components/common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useCancellationPolicyRules from "hooks/cancellation-policy/useCancellationPolicyRules";
import CancellationPolicyFormModal from "./CancellationPolicyFormModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const statusBadge = (active) => (active ? "success" : "secondary");

const CancellationPolicyHistoryPage = () => {
  const { loading, getAllRules, createRule, updateRule, deleteRule } =
    useCancellationPolicyRules();

  const [rules, setRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [mutating, setMutating] = useState(false);

  const loadAllRules = useCallback(async () => {
    try {
      const data = await getAllRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load cancellation rule history");
    }
  }, [getAllRules]);

  useEffect(() => {
    loadAllRules();
  }, [loadAllRules]);

  const filteredRules = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return rules;

    return rules.filter((rule) => {
      const bucket = [
        rule?.id,
        rule?.stage,
        rule?.canceler,
        rule?.chargeBase,
        rule?.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return bucket.includes(keyword);
    });
  }, [rules, searchTerm]);

  const onCreate = async (payload) => {
    setMutating(true);
    try {
      await createRule(payload);
      toast.success("Cancellation rule created");
      setShowForm(false);
      setEditingRule(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to create rule");
    } finally {
      setMutating(false);
    }
  };

  const onUpdate = async (payload) => {
    if (!editingRule?.id) return;
    setMutating(true);
    try {
      await updateRule(editingRule.id, payload);
      toast.success("Cancellation rule updated");
      setShowForm(false);
      setEditingRule(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to update rule");
    } finally {
      setMutating(false);
    }
  };

  const onDelete = async () => {
    if (!deletingRuleId) return;
    setMutating(true);
    try {
      await deleteRule(deletingRuleId);
      toast.success("Cancellation rule deleted");
      setShowDelete(false);
      setDeletingRuleId(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to delete rule");
      throw err;
    } finally {
      setMutating(false);
    }
  };

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4"
        title="Cancellation Policy History"
        icon="table"
        subtitle="Browse all cancellation rules and manage create, update, and delete operations."
      />

      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <Input
          placeholder="Search by id, stage, canceler, description..."
          value={searchTerm}
          allowClear
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 360 }}
        />

        <IconButton
          variant="falcon-default"
          size="sm"
          icon="plus"
          onClick={() => {
            setEditingRule(null);
            setShowForm(true);
          }}
        >
          <span className="ms-1">Add Rule</span>
        </IconButton>
      </div>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220 }}>
              <Spinner animation="border" variant="success" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="py-5">
              <Empty description="No cancellation rules found" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Canceler</th>
                    <th>Status</th>
                    <th>Charge</th>
                    <th>Effective From</th>
                    <th>Effective To</th>
                    <th>ID</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.map((rule) => (
                    <tr key={rule.id}>
                      <td>{rule.stage || "-"}</td>
                      <td>{rule.canceler || "-"}</td>
                      <td>
                        <Badge bg={statusBadge(rule.active)}>
                          {rule.active ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </td>
                      <td>
                        {rule.chargePercent ?? 0}% + {rule.chargeFixedAmount ?? 0}
                      </td>
                      <td>{formatDateTime(rule.effectiveFrom)}</td>
                      <td>{formatDateTime(rule.effectiveTo)}</td>
                      <td className="font-monospace" style={{ fontSize: 12 }}>
                        {rule.id}
                      </td>
                      <td className="text-center">
                        <Dropdown>
                          <Dropdown.Toggle as="button" className="bg-transparent border-0 text-secondary">
                            ...
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => {
                                setEditingRule(rule);
                                setShowForm(true);
                              }}
                            >
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => {
                                setDeletingRuleId(rule.id);
                                setShowDelete(true);
                              }}
                            >
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <CancellationPolicyFormModal
        show={showForm}
        onClose={() => {
          if (mutating) return;
          setShowForm(false);
          setEditingRule(null);
        }}
        onSubmit={editingRule ? onUpdate : onCreate}
        loading={mutating}
        initialRule={editingRule}
        title={editingRule ? "Edit Cancellation Rule" : "Create Cancellation Rule"}
      />

      <ConfirmDeleteModal
        show={showDelete}
        setShow={setShowDelete}
        onConfirm={onDelete}
        itemName="cancellation policy rule"
        loading={mutating}
      />
    </>
  );
};

export default CancellationPolicyHistoryPage;
